import React, { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { DocumentEntityType, DocumentType } from "../../../../../api/caseDocuments";
import {
  Assessment,
  AssessmentUI,
  FraudAngleRecommendation,
  IndividualAssessor,
  IOAppointingAuthority,
  SavePostIRAssessmentsRequest,
  ZonalCommittee,
} from "../../../../../api/observationByAuthority";
import { useAppDispatch } from "../../../../../app/hooks";
import { useAppSelector } from "../../../../../app/store";
import { ErrorDisplay } from "../../../../../components/ui/error/ErrorDisplay";
import { showErrorToast, showSuccessToast } from "../../../../../lib/toast";
import ReportActionHeader from "../../common/ReportActionHeader";
import { ReportDetailsShimmering } from "../../common/ReportDetailsShimmering";
import ReportDocuments from "../../../../documentManagement/components/ReportDocuments";
import ReportHeader from "../../common/ReportHeader";
import ViewOrAddRecommendation from "../../common/ViewOrAddRecommendation";
import { getAssessment, getFetchError, getFetchStatus } from "../logic/ObservationsByAuthoritySlice";
import { fetchAssessments, saveAssessments } from "../logic/ObservationsByAuthorityThunk";
import CompetentAuthorityDropdown from "./CompetentAuthorityDropdown";
import ObservationsByAuthorityEmptyState from "./ObservationsByAuthorityEmptyState";
import ObservationsForm from "./ObservationsForm";
import SpecificCommentsOnFindingsForm from "./SpecificCommentsOnFindingsForm";
import { IOAppAuthSubmitAssessmentStageButton } from "../../../../../../src/features/caseStages/components/stage_ctas/IOAppAuthSubmitAssessmentStageButton";



const createEmptyAssessment = (): AssessmentUI => ({
  assessment_id: undefined,
  competent_authority: undefined,
  assessment_date: undefined,
  recommendation: undefined,
  meeting_minutes: null,
  deliberation_from_convener: null,
  borrower_or_perpatrator_kyc_assessment: null,
  primary_security_assessment: null,
  collateral_security_assessment: null,
  end_use_of_funds_assessment: null,
  business_operation_assessment: null,
  io_findings_assessment: null,
  isDirty: false,
  isAdded: true,
});

const isAssessmentValid = (a: AssessmentUI): boolean =>
  !!a.competent_authority && !!a.recommendation && (!a.isAdded || !!a.assessment_date);

const mapStoreToUI = (s: Assessment): AssessmentUI => ({
  assessment_id: s.assessment_id,
  competent_authority:
    s.authority === ZonalCommittee.ZFRC
      ? IOAppointingAuthority.ZFRC
      : (s.authority as unknown as IOAppointingAuthority),
  assessment_date: undefined,
  recommendation: s.recommendation,
  meeting_minutes: "meeting_minutes" in s ? s.meeting_minutes : null,
  deliberation_from_convener: "deliberation_from_convener" in s ? s.deliberation_from_convener : null,
  borrower_or_perpatrator_kyc_assessment: s.borrower_or_perpatrator_kyc_assessment,
  primary_security_assessment: s.primary_security_assessment,
  collateral_security_assessment: s.collateral_security_assessment,
  end_use_of_funds_assessment: s.end_use_of_funds_assessment,
  business_operation_assessment: s.business_operation_assessment,
  io_findings_assessment: s.io_findings_assessment,
  isDirty: false,
  isAdded: false,
});

const isZFRC = (authority?: IOAppointingAuthority): boolean =>
  authority === IOAppointingAuthority.ZFRC;

const buildSavePayload = (assessment: AssessmentUI): SavePostIRAssessmentsRequest => {
  const comments = {
    borrower_or_perpatrator_kyc_assessment: assessment.borrower_or_perpatrator_kyc_assessment,
    primary_security_assessment: assessment.primary_security_assessment,
    collateral_security_assessment: assessment.collateral_security_assessment,
    end_use_of_funds_assessment: assessment.end_use_of_funds_assessment,
    business_operation_assessment: assessment.business_operation_assessment,
    io_findings_assessment: assessment.io_findings_assessment,
  };

  const isZfrc = isZFRC(assessment.competent_authority);

  if (assessment.isAdded) {
    if (isZfrc) {
      return {
        add_zonal_committee_assessments: [
          {
            ...comments,
            authority: ZonalCommittee.ZFRC,
            assessment_date: assessment.assessment_date!,
            recommendation: assessment.recommendation!,
            meeting_minutes: assessment.meeting_minutes,
            deliberation_from_convener: assessment.deliberation_from_convener,
          },
        ],
      };
    } else {
      return {
        add_appointing_auth_assessments: [
          {
            ...comments,
            authority: assessment.competent_authority! as unknown as IndividualAssessor,
            assessment_date: assessment.assessment_date!,
            recommendation: assessment.recommendation!,
          },
        ],
      };
    }
  } else {
    if (isZfrc) {
      return {
        update_zonal_committee_assessments: [
          {
            ...comments,
            assessment_id: assessment.assessment_id!,
            recommendation: assessment.recommendation as FraudAngleRecommendation | undefined,
            meeting_minutes: assessment.meeting_minutes,
            deliberation_from_convener: assessment.deliberation_from_convener,
          },
        ],
      };
    } else {
      return {
        update_appointing_auth_assessments: [
          {
            ...comments,
            assessment_id: assessment.assessment_id!,
            recommendation: assessment.recommendation as FraudAngleRecommendation | undefined,
          },
        ],
      };
    }
  }
};

const ObservationsByAuthority: React.FC = () => {
  const { id: case_id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { scrollRef } = useOutletContext<{ scrollRef: React.RefObject<HTMLDivElement> }>();

  const [assessment, setAssessment] = useState<AssessmentUI>({
    ...createEmptyAssessment(),
    isAdded: false,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const assessmentFromStore = useAppSelector(getAssessment);
  const fetchStatus = useAppSelector(getFetchStatus);
  const fetchError = useAppSelector(getFetchError);

  const isPageLoading = fetchStatus === "loading";
  const isPageError = fetchStatus === "failed";
  const isSaved = !!assessment.assessment_id;
  const showHeaderButtons = !!assessment.competent_authority || isSaved;
  const showForm = assessment.isAdded || !!assessment.assessment_id;

  useEffect(() => {
    if (!case_id) return;
    dispatch(fetchAssessments(case_id));
  }, [case_id, dispatch]);

  useEffect(() => {
    if (fetchStatus !== "succeeded") return;

    if (!assessmentFromStore) {
        setAssessment({ ...createEmptyAssessment(), isAdded: false });
        setIsDirty(false);
    } else {
      setAssessment(mapStoreToUI(assessmentFromStore));
      setIsDirty(false);
    }

  }, [assessmentFromStore, fetchStatus]);

  const handleFieldChange = <K extends keyof AssessmentUI>(key: K, value: AssessmentUI[K]) => {
    setAssessment((prev) => {
      const updated = { ...prev, [key]: value, isDirty: true };
      const valid = isAssessmentValid(updated);
      if (valid !== isFormValid) {
        setIsFormValid(valid);
      }
      return updated;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!case_id || !isAssessmentValid(assessment)) return;
    setIsSaving(true);
    try {
      const payload = buildSavePayload(assessment);
      await dispatch(saveAssessments({ case_id, payload })).unwrap();
      setIsDirty(false);
      setIsFormValid(true);
      showSuccessToast("Observations saved successfully");
    } catch (error) {
      showErrorToast("Failed to save observations. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!assessmentFromStore) {
      setAssessment({ ...createEmptyAssessment(), isAdded: false });
      setIsDirty(false);
      return;
    }
    setAssessment(mapStoreToUI(assessmentFromStore));
    setIsDirty(false);
  };

  const handleRetry = () => {
    if (!case_id) return;
    dispatch(fetchAssessments(case_id));
  };

  const renderForm = () => (
    <>
      <ReportHeader title="Observations by Authority" actions={<IOAppAuthSubmitAssessmentStageButton />} />
      <div className="px-6 py-4 min-h-[80vh]">
        <CompetentAuthorityDropdown
          value={assessment.competent_authority}
          onChange={(val) => handleFieldChange("competent_authority", val as IOAppointingAuthority)}
          disabled={isSaved}
        />

        {!!assessment.competent_authority && (
          <>
            <ObservationsForm assessment={assessment} isSaved={isSaved} onFieldChange={handleFieldChange} />
            <SpecificCommentsOnFindingsForm assessment={assessment} onFieldChange={handleFieldChange} />
            <div className="mb-7">
              <ReportDocuments
                document_type={DocumentType.IO_APPOINTING_AUTH_REVIEW}
                entityId={assessment.assessment_id ?? ""}
                entityType={DocumentEntityType.IO_APPOINTING_AUTH_ASSESSMENT}
                disabledUploadButton={!isSaved}
              />
              {!isSaved && (
                <p className="text-xs text-gray-500 mt-2">Save your observations to enable document uploads</p>
              )}
            </div>
            <div className="mb-4">
              {case_id && (
                <ViewOrAddRecommendation
                  caseId={case_id}
                  scrollRef={scrollRef}
                  actionAgainstInvolvedPartiesDisabled={!isSaved}
                  processAndPolicyImprovementsDisabled={!isSaved}
                />
              )}
              {!isSaved && <p className="text-xs text-gray-500 mt-2">Save your observations to add recommendations</p>}
            </div>
          </>
        )}
      </div>
    </>
  );

  const renderContent = () => {
    if (isPageLoading) return <ReportDetailsShimmering sectionCount={2} />;
    if (isPageError)
      return (
        <ErrorDisplay
          error={fetchError}
          title="Failed to load Observations by Authority"
          onRetry={handleRetry}
          className="m-1"
        />
      );
    if (!showForm)
      return (
        <>
          <ReportHeader title="Observations by Authority" />
          <ObservationsByAuthorityEmptyState onAddObservations={() => setAssessment(createEmptyAssessment())} />
        </>
      );
    return renderForm();
  };

  return (
    <div className="w-full h-full overflow-auto bg-[#F3F4F6]">
      {showHeaderButtons && (
        <div className="sticky top-0 left-0 right-0 flex justify-end items-center px-6 py-4 bg-[#F3F4F6] z-10">
          <ReportActionHeader
            isPageLoading={isPageLoading}
            isSaving={isSaving}
            disableSave={!isDirty || !isFormValid}
            disableDiscard={!isDirty}
            handleSave={handleSave}
            handleDiscard={handleDiscard}
          />
        </div>
      )}
      <div className="max-w-8xl mx-6 bg-white rounded-xl">{renderContent()}</div>
      <div className="h-14" />
    </div>
  );
};

export default ObservationsByAuthority;
